import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Globe } from "lucide-react";
import { getAuthHeader } from "@/lib/authHelpers";

import { useI18n } from "@/lib/i18n";
import type { AppLocale } from "@shared/i18n-locales";

type TranslationTable = "shops" | "gems" | "reviews" | "comments" | "routes" | "shop_posts";

interface TranslatableTextProps {
  table: TranslationTable;
  rowId: string;
  text?: string | null;
  translations?: Record<string, string> | null;
  as?: "p" | "span" | "div";
  className?: string;
}

export function TranslatableText({
  table,
  rowId,
  text,
  translations,
  as = "p",
  className,
}: TranslatableTextProps) {
  const { t, locale } = useI18n();
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const targetLanguage = useMemo(() => {
    // Use the app's selected locale — not the browser language.
    // Users who switch the app to English expect Dutch content to be translatable.
    return locale as AppLocale;
  }, [locale]);

  const targetLanguageLabel = useMemo(() => {
    try {
      const formatter = new Intl.DisplayNames([locale], { type: "language" });
      return formatter.of(targetLanguage) || targetLanguage.toUpperCase();
    } catch {
      return targetLanguage.toUpperCase();
    }
  }, [locale, targetLanguage]);

  const dbTranslation = translations?.[targetLanguage];
  const finalText = translatedText || dbTranslation || text || "";
  // Don't show translation button if target is Dutch, assuming original is already Dutch in this app
  const isRedundant = targetLanguage === "nl";
  const canTranslate = Boolean(text && rowId && targetLanguage && !dbTranslation && !translatedText && !isRedundant);
  const Tag = as;

  const handleTranslate = async () => {
    if (!text || !rowId || !canTranslate || isLoading) return;
    setError(null);
    setIsLoading(true);
    try {
      const headers = await getAuthHeader();
      if (!headers) {
        setError(t("translate.loginRequired"));
        return;
      }

      const response = await fetch("/api/translate", {
        method: "POST",
        headers,
        body: JSON.stringify({
          text,
          targetLanguage,
          table,
          rowId,
        }),
      });

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result?.error || t("translate.failed"));
      }

      const result = await response.json();
      setTranslatedText(result.translation || "");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("translate.failed"));
    } finally {
      setIsLoading(false);
    }
  };

  if (!text) return null;

  return (
    <div className="space-y-1">
      <Tag className={className}>{finalText}</Tag>
      {canTranslate && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-xs text-magenta border-magenta hover:bg-magenta hover:text-white transition-colors flex items-center gap-1.5"
          onClick={handleTranslate}
          disabled={isLoading}
        >
          <Globe className="h-3.5 w-3.5" />
          {isLoading ? t("translate.loading") : t("translate.to", { language: targetLanguageLabel })}
        </Button>

      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
